import React, { useState, useEffect, useCallback } from 'react';
import './FormulaIntelligencePage.css';
import formulaService from '../../../services/formulaService';
import { masterService } from '../../../services/masterService';
import FormulaLibraryGrid from './components/FormulaLibraryGrid';
import FormulaStudio from './components/FormulaStudio';
import FormulaImpactModal from './components/FormulaImpactModal';
import FormulaAuditModal from './components/FormulaAuditModal';

export const FormulaIntelligencePage = () => {
  // Navigation State
  const [activeTab, setActiveTab] = useState('library'); // 'library' | 'studio'
  const [editingFormula, setEditingFormula] = useState(null);

  // Modals State
  const [impactModalFormula, setImpactModalFormula] = useState(null);
  const [auditModalFormula, setAuditModalFormula] = useState(null);

  // Real Database Stats
  const [stats, setStats] = useState({
    totalFormulas: 0,
    activeFormulas: 0,
    draftFormulas: 0
  });

  const [formulas, setFormulas] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filter State
  const [search, setSearch] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [investigationId, setInvestigationId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);

  // Real LIMS Masters
  const [departments, setDepartments] = useState([]);
  const [investigations, setInvestigations] = useState([]);

  // Fetch Pathology Departments
  useEffect(() => {
    formulaService.getDashboardStats()
      .then(res => {
        if (res) {
          setStats({
            totalFormulas: res.totalFormulas || 0,
            activeFormulas: res.activeFormulas || 0,
            draftFormulas: res.draftFormulas || 0
          });
        }
      })
      .catch(err => console.error('Failed to load formula dashboard stats:', err));

    masterService.getDepartments()
      .then(res => setDepartments(res?.data || res || []))
      .catch(err => console.error('Failed to load departments:', err));
  }, []);

  // Filter departments to ONLY Pathology
  const pathologyDepartments = departments.filter(d => {
    if (d.categoryId === 1) return true;
    const name = (d.name || d.displayName || '').toUpperCase();
    return (
      name.includes('PATH') ||
      name.includes('BIOCHEM') ||
      name.includes('HEMAT') ||
      name.includes('SEROL') ||
      name.includes('HISTO') ||
      name.includes('CYTO') ||
      name.includes('COAG') ||
      name.includes('BLOOD') ||
      name.includes('GENETIC')
    );
  });

  // When Department changes in the filter toolbar -> cascade load real investigations
  useEffect(() => {
    if (departmentId) {
      masterService.getInvestigations({
        subCategoryId: parseInt(departmentId, 10),
        isActive: true,
        pageSize: 100
      })
        .then(res => setInvestigations(res?.data?.items || res?.items || res?.data || []))
        .catch(err => console.error(err));
    } else {
      setInvestigations([]);
      setInvestigationId('');
    }
  }, [departmentId]);

  // Fetch Formula List from Database
  const fetchFormulas = useCallback(async () => {
    setLoading(true);
    try {
      const res = await formulaService.getFormulas({
        search: search.trim() || undefined,
        departmentId: departmentId ? parseInt(departmentId, 10) : undefined,
        investigationId: investigationId ? parseInt(investigationId, 10) : undefined,
        status: statusFilter || undefined,
        page,
        pageSize: 20
      });

      if (res) {
        setFormulas(res.items || []);
        setTotalCount(res.totalCount || 0);
      }
    } catch (err) {
      console.error('Failed to load formulas:', err);
      setFormulas([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [search, departmentId, investigationId, statusFilter, page]);

  useEffect(() => {
    fetchFormulas();
  }, [fetchFormulas]);

  const refreshStats = () => {
    formulaService.getDashboardStats()
      .then(res => {
        if (res) {
          setStats({
            totalFormulas: res.totalFormulas || 0,
            activeFormulas: res.activeFormulas || 0,
            draftFormulas: res.draftFormulas || 0
          });
        }
      })
      .catch(err => console.error(err));
  };

  // Actions
  const handleEdit = async (formula) => {
    try {
      const detail = await formulaService.getFormulaById(formula.formulaId);
      setEditingFormula(detail || formula);
      setActiveTab('studio');
    } catch (err) {
      console.error('Failed to fetch formula details for edit:', err);
      setEditingFormula(formula);
      setActiveTab('studio');
    }
  };

  const handleCreateNew = () => {
    setEditingFormula(null);
    setActiveTab('studio');
  };

  const handleToggleStatus = async (formula) => {
    try {
      if (formula.status === 'Active') {
        const reason = window.prompt('Please enter deactivation reason:');
        if (!reason) return;
        await formulaService.deactivateFormula(formula.formulaId, reason);
      } else {
        await formulaService.activateFormula(formula.formulaId);
      }
      fetchFormulas();
      refreshStats();
    } catch (err) {
      alert(`Error toggling status: ${err.response?.data?.message || err.message}`);
    }
  };

  return (
    <div className="formula-intel-container">
      {/* ── Top Header ────────────────────────────────────────── */}
      <div className="formula-header">
        <div className="formula-header-title">
          <h1>
            <span>Pathology Formula Master</span>
            <span className="badge-ai" style={{ background: '#2563eb' }}>Enterprise LIMS</span>
          </h1>
          <p>
            Configuration and mathematical engine for Pathology calculated parameters and clinical rules.
          </p>
        </div>
        <div className="formula-header-actions">
          <button
            className="btn-primary-gradient"
            onClick={handleCreateNew}
          >
            + Create New Formula
          </button>
        </div>
      </div>

      {/* ── Stats Metric Cards (Derived From Real DB) ─────────── */}
      <div className="formula-stats-grid">
        <div className="stat-card">
          <span className="stat-card-label">Total Formula Registry</span>
          <span className="stat-card-value">{stats.totalFormulas}</span>
          <span className="stat-card-indicator indicator-blue">● Real Database Records</span>
        </div>
        <div className="stat-card">
          <span className="stat-card-label">Active Formulas</span>
          <span className="stat-card-value">{stats.activeFormulas}</span>
          <span className="stat-card-indicator indicator-green">● Auto-Triggering in Result Entry</span>
        </div>
        <div className="stat-card">
          <span className="stat-card-label">Drafts & Staging</span>
          <span className="stat-card-value">{stats.draftFormulas}</span>
          <span className="stat-card-indicator indicator-amber">● Inactive / In Review</span>
        </div>
      </div>

      {/* ── Navigation Tabs ───────────────────────────────────── */}
      <div className="formula-tabs-bar">
        <button
          className={`formula-tab-btn ${activeTab === 'library' ? 'active' : ''}`}
          onClick={() => setActiveTab('library')}
        >
          📚 Formula Registry ({totalCount})
        </button>
        <button
          className={`formula-tab-btn ${activeTab === 'studio' ? 'active' : ''}`}
          onClick={() => setActiveTab('studio')}
        >
          🧪 Formula Studio {editingFormula ? '(Editing)' : ''}
        </button>
      </div>

      {/* ── Tab Content ───────────────────────────────────────── */}
      {activeTab === 'library' && (
        <>
          {/* Cascading Filter Toolbar */}
          <div className="formula-toolbar">
            <div className="toolbar-search">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input
                type="text"
                placeholder="Search formulas by name, code, or description..."
                value={search}
                onChange={e => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>

            <div className="toolbar-filters">
              {/* Department Filter (Pathology Only) */}
              <select
                className="toolbar-select"
                value={departmentId}
                onChange={e => {
                  setDepartmentId(e.target.value);
                  setInvestigationId('');
                  setPage(1);
                }}
              >
                <option value="">All Pathology Departments</option>
                {pathologyDepartments.map(d => (
                  <option key={d.subCategoryId} value={d.subCategoryId}>
                    {d.displayName || d.name}
                  </option>
                ))}
              </select>

              {/* Investigation Filter */}
              <select
                className="toolbar-select"
                value={investigationId}
                disabled={!departmentId}
                onChange={e => {
                  setInvestigationId(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">
                  {!departmentId ? 'All Investigations' : 'All Investigations in Dept'}
                </option>
                {investigations.map(inv => (
                  <option key={inv.investigationId || inv.id} value={inv.investigationId || inv.id}>
                    {inv.investigationName || inv.name} ({inv.testCode})
                  </option>
                ))}
              </select>

              {/* Status Filter */}
              <select
                className="toolbar-select"
                value={statusFilter}
                onChange={e => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All Statuses</option>
                <option value="Active">Active</option>
                <option value="Draft">Draft</option>
                <option value="Inactive">Inactive</option>
              </select>

              <button
                className="btn-secondary-dark"
                style={{ color: '#475569', borderColor: '#cbd5e1' }}
                onClick={() => {
                  setSearch('');
                  setDepartmentId('');
                  setInvestigationId('');
                  setStatusFilter('');
                  setPage(1);
                }}
              >
                Reset Filters
              </button>
            </div>
          </div>

          {/* Grid */}
          <FormulaLibraryGrid
            formulas={formulas}
            loading={loading}
            onCreateNew={handleCreateNew}
            onEdit={handleEdit}
            onTest={f => handleEdit(f)}
            onViewImpact={f => setImpactModalFormula(f)}
            onViewVersions={f => setAuditModalFormula(f)}
            onToggleStatus={handleToggleStatus}
          />
        </>
      )}

      {activeTab === 'studio' && (
        <FormulaStudio
          editingFormula={editingFormula}
          departments={departments}
          onSaved={() => {
            setActiveTab('library');
            setEditingFormula(null);
            fetchFormulas();
            refreshStats();
          }}
          onCancel={() => {
            setActiveTab('library');
            setEditingFormula(null);
          }}
        />
      )}

      {/* ── Modals ────────────────────────────────────────────── */}
      {impactModalFormula && (
        <FormulaImpactModal
          formula={impactModalFormula}
          onClose={() => setImpactModalFormula(null)}
        />
      )}

      {auditModalFormula && (
        <FormulaAuditModal
          formula={auditModalFormula}
          onClose={() => setAuditModalFormula(null)}
        />
      )}
    </div>
  );
};

export default FormulaIntelligencePage;
